import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import PageShell from "@/components/layout/PageShell";
import ServerPagination from "@/components/ServerPagination";
import { Package, Search, PackageCheck, PenLine, Globe, Copy, Zap, Edit3, Trash2, X, Save, Layers, Split, Mail, PackageOpen } from "lucide-react";
import DeliveryReceiptDialog, { DownloadReceiptButton } from "@/components/DeliveryReceiptDialog";
import SplitDeliveryDialog from "@/components/SplitDeliveryDialog";
import EmptyState from "@/components/EmptyState";
import PickupRequestButton from "@/components/PickupRequestButton";
import ItemBreakdownEditor from "@/components/ItemBreakdownEditor";
import CourierSelect from "@/components/CourierSelect";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import TatChip from "@/components/TatChip";
import ShipmentTypeRadio from "@/components/ShipmentTypeRadio";
import BookParcelDialog from "@/components/BookParcelDialog";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["received","in_transit","out_for_delivery","delivered","forwarded","returned","rto"];

// Distinctive Return-To-Office badge; other statuses use the plain uppercase tracking-widest chip.
const RTO_BADGE_CLASSES = "text-[10px] uppercase tracking-widest font-bold bg-primary text-primary-foreground border border-primary px-2 py-0.5 rounded";

const StatusPill = ({ s }) => {
  const map = {
    received: "bg-accent/20 border-accent",
    in_transit: "bg-primary/10 border-primary text-primary",
    delivered: "bg-emerald-500/15 border-emerald-500 text-emerald-700",
    forwarded: "bg-yellow-500/15 border-yellow-600 text-yellow-800",
    out_for_delivery: "bg-primary/15 border-primary text-primary",
    returned: "bg-destructive/10 border-destructive text-destructive",
    rto: "bg-primary text-primary-foreground border-primary font-bold",
  };
  if (s === "rto") {
    return <span data-testid="badge-rto" className={RTO_BADGE_CLASSES}>Return to Office (RTO)</span>;
  }
  return <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${map[s]||"border-border"}`}>{s?.replace(/_/g," ")}</span>;
};

export default function ParcelsPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [signedBy, setSignedBy] = useState("");
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // parcel object to be deleted
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem("parcels.pageSize") || "50", 10));
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  // Batch selection for Book Parcel workflow
  const [selectedIds, setSelectedIds] = useState({});   // { [parcel_id]: true }
  const [bookOpen, setBookOpen] = useState(false);
  const { user } = useAuth();
  const canDelete = user && ["branch_admin", "company_admin", "super_admin"].includes(user.role);

  const direction = params.get("direction") || "";
  const status = params.get("status") || "";

  const load = async (nextPage = page, nextSize = pageSize) => {
    const { data } = await api.get("/parcels-paginated", { params: {
      direction: direction || undefined, status: status || undefined,
      q: q || undefined, page: nextPage, page_size: nextSize,
    }});
    setItems(data.items || []);
    setTotal(data.total || 0); setPages(data.pages || 1);
  };
  useEffect(() => { setPage(1); load(1, pageSize); /* eslint-disable-next-line */ }, [direction, status]);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);

  const markStatus = async (id, newStatus) => {
    try {
      await api.patch(`/parcels/${id}/status`, { status: newStatus, remarks, signed_by: signedBy });
      toast.success(`Marked ${newStatus}`);
      setSelected(null); setRemarks(""); setSignedBy("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (target) => {
    const src = target || selected;
    if (!src) return;
    setEdit({
      tracking_number: src.tracking_number || "",
      awb_number: src.awb_number || "",
      courier: src.courier || "",
      sender_name: src.sender_name || "",
      sender_phone: src.sender_phone || "",
      recipient_name: src.recipient_name || "",
      recipient_department: src.recipient_department || "",
      recipient_phone: src.recipient_phone || "",
      weight_kg: src.weight_kg || 0.5,
      item_count: src.item_count || 1,
      dimensions: src.dimensions || "",
      notes: src.notes || "",
      items: Array.isArray(src.items) ? src.items : [],
    });
    setEditing(true);
  };
  // Row double-click → open detail dialog AND immediately switch to Edit mode (reuses startEdit).
  const openInEdit = (p) => {
    if (!p) return;
    if (p.status === "delivered" || p.status === "returned") { setSelected(p); return; }
    setSelected(p);
    startEdit(p);
  };
  const saveEdit = async () => {
    // Feb 2026 · Branch User edit-vs-create hardening.
    // Guarantee we PATCH the existing record and never fall through to a Create.
    // Delete permission is NOT required to edit — PATCH stands on its own on the backend.
    if (!selected?.id) {
      toast.error("No parcel selected — cannot save edit.");
      return;
    }
    if (!edit || Object.keys(edit).length === 0) {
      toast.error("Nothing to update.");
      return;
    }
    try {
      // Never send an `id` inside the body — backend keys off the URL path only.
      const { id: _drop, ...safeEdit } = edit;
      const { data: patched } = await api.patch(`/parcels/${selected.id}`, safeEdit);
      // Backend returns {ok:true} — fetch the fresh row for the UI.
      if (patched && patched.ok !== undefined && !patched.id) {
        const { data } = await api.get(`/parcels/${selected.id}`);
        setSelected(data);
      } else if (patched && patched.id) {
        setSelected(patched);
      }
      toast.success("Parcel updated");
      setEditing(false); setEdit(null);
      load();
    } catch (e) {
      // Do NOT fall back to POST — surface the real error so we never create a duplicate.
      const msg = e?.response?.data?.detail || e?.message || "Failed to update parcel";
      toast.error(`Update failed: ${msg}`);
    }
  };
  const deleteParcel = () => {
    if (!selected) return;
    setPendingDelete(selected);
  };
  const [emailingId, setEmailingId] = useState(null);
  const emailStatus = async (p, extra) => {
    if (!p) return;
    setEmailingId(p.id);
    const doSend = async (to) => {
      const params = to ? { to } : {};
      const { data } = await api.post(`/parcels/${p.id}/email-status`, null, { params });
      const sent = (data?.sent_to || []).length;
      if (sent === 0) toast.error("Email queued but SMTP is not configured — no message left the server");
      else toast.success(`Status emailed to ${sent} recipient${sent > 1 ? "s" : ""}`);
    };
    try {
      await doSend(extra);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Email failed";
      // Auto-fallback: no email on parcel → ask the operator once
      if (e?.response?.status === 422 && /no recipient/i.test(detail)) {
        const suggested = p.recipient_email || p.sender_email || "";
        // eslint-disable-next-line no-alert
        const answer = window.prompt(`This parcel has no sender/recipient email on file.\nEnter one or more email addresses (comma-separated) to send the status to:`, suggested);
        if (answer && answer.trim()) {
          try { await doSend(answer.trim()); }
          catch (e2) { toast.error(e2?.response?.data?.detail || "Email failed"); }
        }
      } else {
        toast.error(detail);
      }
    } finally { setEmailingId(null); }
  };
  const confirmDeleteParcel = async (reason) => {
    const target = pendingDelete;
    if (!target) return false;
    try {
      await api.delete(`/parcels/${target.id}`, { params: { reason: reason || "" } });
      toast.success("Parcel deleted");
      if (selected?.id === target.id) setSelected(null);
      setPendingDelete(null);
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return false;
    }
  };

  return (
    <PageShell
      title="Parcels."
      overline="Chain-of-custody ledger"
      testid="parcels"
      meta={total > 0 && (
        <span className="text-[10px] uppercase tracking-widest border border-border bg-muted/50 px-2 py-0.5 rounded font-mono" data-testid="parcels-count-pill">
          {total.toLocaleString()} on record
        </span>
      )}
      actions={
        <>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="parcels-search" value={q} onChange={(e)=>setQ(e.target.value)} onKeyDown={(e)=>{if(e.key==="Enter"){ setPage(1); load(1, pageSize);} }}
              className="pl-8 rounded-md border-input w-48 md:w-64" placeholder="Tracking, AWB, ref, booking, sender, recipient, mobile, courier…" />
          </div>
          <Button data-testid="parcels-search-btn" onClick={() => { setPage(1); load(1, pageSize); }} className="rounded-md bg-foreground text-background hover-brut shadow-brut">Search</Button>
          {Object.keys(selectedIds).filter(k => selectedIds[k]).length > 0 ? (
            <Button
              data-testid="book-parcel-btn"
              onClick={() => setBookOpen(true)}
              className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"
            >
              <PackageOpen size={14} className="mr-1" />
              Book Parcel · {Object.keys(selectedIds).filter(k => selectedIds[k]).length}
            </Button>
          ) : (
            <Button
              data-testid="book-parcel-btn-empty"
              onClick={() => toast.error("Please select at least one parcel to continue.")}
              className="rounded-md bg-primary/90 text-primary-foreground shadow-brut-orange"
            >
              <PackageOpen size={14} className="mr-1" />
              Book Parcel
            </Button>
          )}
        </>
      }
    >

      <div className="flex flex-col h-[calc(100vh-192px)] -mt-4">
      <div
        className="shrink-0 -mx-4 md:-mx-6 px-4 md:px-6 py-2 border-b border-border bg-white/95 backdrop-blur flex flex-wrap gap-2 text-xs"
        data-testid="parcels-filters"
      >
        {[["all",""],["inward","inward"],["outward","outward"]].map(([k,v]) => (
          <button key={k} data-testid={`filter-dir-${k}`} onClick={() => { const n=new URLSearchParams(params); if(v) n.set("direction",v); else n.delete("direction"); setParams(n); }}
            className={`px-3 py-1 border ${direction===v ? "bg-primary text-primary-foreground border-primary" : "border-border"} uppercase tracking-widest`}>{k}</button>
        ))}
        <span className="w-3" />
        {["", ...STATUSES].map((s) => (
          <button key={s||"all"} data-testid={`filter-status-${s||"all"}`} onClick={() => { const n=new URLSearchParams(params); if(s) n.set("status",s); else n.delete("status"); setParams(n); }}
            className={`px-3 py-1 border ${status===s ? "bg-foreground text-background border-foreground" : "border-border"} uppercase tracking-widest`}>{s||"any status"}</button>
        ))}
      </div>

      <div className="flex-1 min-h-0 border border-border bg-card overflow-auto mt-2" data-testid="parcels-table-scroll">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-[3] bg-muted/60 backdrop-blur">
            <tr className="border-b border-border text-left overline">
              <th className="p-4 w-8">
                <input
                  type="checkbox"
                  data-testid="parcels-select-all"
                  className="accent-[#BE1E1E]"
                  checked={(() => {
                    const outwardOnPage = items.filter(p => p.direction === "outward");
                    return outwardOnPage.length > 0 &&
                      outwardOnPage.every(p => selectedIds[p.id]);
                  })()}
                  onChange={(e) => {
                    const outwardOnPage = items.filter(p => p.direction === "outward");
                    const next = { ...selectedIds };
                    outwardOnPage.forEach(p => { next[p.id] = e.target.checked; });
                    setSelectedIds(next);
                  }}
                />
              </th>
              <th className="p-4">Tracking</th><th className="p-4">Dir.</th><th className="p-4">Courier</th>
              <th className="p-4">Consignor → Consignee</th>
              <th className="p-4">Type</th>
              <th className="p-4">Chg. Wt</th>
              <th className="p-4">Gross ₹</th>
              <th className="p-4">Status</th>
              <th className="p-4">Booking</th>
              <th className="p-4">ETA / SLA</th>
              <th className="p-4">Created</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="parcels-table">
            {items.length === 0 && (
              <tr><td colSpan={13} className="p-0">
                <EmptyState
                  icon={Package}
                  variant="package"
                  title="No parcels found"
                  subtitle={q || direction || status ? "Try clearing filters or search." : "New parcels will appear here."}
                  action={<Link to="/app/inward" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-md shadow-brut-orange text-sm font-semibold">+ Log an Inward parcel</Link>}
                  testid="empty-parcels"
                />
              </td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-b border-border hover:bg-muted/40 cursor-pointer"
                  onDoubleClick={() => openInEdit(p)}
                  data-testid={`parcel-row-${p.tracking_number}`}
                  title="Double-click to edit">
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  {p.direction === "outward" && p.booking_status !== "delivered" && (
                    <input
                      type="checkbox"
                      data-testid={`parcel-select-${p.tracking_number}`}
                      className="accent-[#BE1E1E]"
                      checked={!!selectedIds[p.id]}
                      onChange={(e) => setSelectedIds({ ...selectedIds, [p.id]: e.target.checked })}
                    />
                  )}
                </td>
                <td className="p-4 mono text-xs">
                  <div className="flex items-center gap-1.5">
                    {p.priority && <span title="Priority" data-testid={`priority-badge-${p.tracking_number}`} className="inline-flex items-center gap-0.5 bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"><Zap size={9} strokeWidth={3} /> P</span>}
                    <span>{p.tracking_number}</span>
                  </div>
                </td>
                <td className="p-4"><span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 ${p.direction==="inward" ? "border-accent" : "border-primary text-primary"}`}>{p.direction}</span></td>
                <td className="p-4 mono">{p.courier}</td>
                <td className="p-4">
                  <div>{p.sender_name}</div>
                  <div className="text-xs text-muted-foreground">→ {p.recipient_name}</div>
                  {p.scope === "International" && (
                    <div className="mt-1 text-[9px] uppercase tracking-widest border border-primary text-primary bg-red-50 inline-block px-1.5 py-0.5 rounded font-bold" title={`Customs value ₹${p.customs_value_inr || 0}`}>
                      Intl · ₹{Number(p.customs_value_inr || 0).toLocaleString("en-IN")}
                    </div>
                  )}
                </td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest border px-2 py-0.5 mono ${p.shipment_type === "Official" ? "border-primary text-primary bg-red-50" : "border-border text-muted-foreground"}`} data-testid={`shipment-type-${p.tracking_number}`}>
                    {p.shipment_type || "Personal"}
                  </span>
                </td>
                <td className="p-4 mono text-right" data-testid={`chargeable-wt-${p.tracking_number}`}>
                  <div>{(p.chargeable_weight_kg ?? p.weight_kg ?? 0)} kg</div>
                  {p.volumetric_weight_kg && p.actual_weight_kg && p.volumetric_weight_kg > p.actual_weight_kg && (
                    <div className="text-[9px] text-muted-foreground">vol · L·B·H</div>
                  )}
                </td>
                <td className="p-4 mono text-right" data-testid={`gross-${p.tracking_number}`}>
                  {p.gross_amount != null
                    ? <span className="font-bold text-primary">₹{Number(p.gross_amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                    : p.rate_inr ? <span className="text-muted-foreground">₹{Number(p.rate_inr).toLocaleString("en-IN")}</span> : <span className="text-muted-foreground">—</span>}
                  {p.gross_amount != null && (p.fuel_surcharge || p.gst_amount) && (
                    <div className="text-[9px] text-muted-foreground">incl. GST</div>
                  )}
                </td>
                <td className="p-4"><StatusPill s={p.status} /></td>
                <td className="p-4">
                  {p.direction === "outward" ? (() => {
                    const bs = p.booking_status || "not_booked";
                    const tone = bs === "not_booked"
                      ? "border-slate-300 text-slate-500"
                      : bs === "cancelled"
                        ? "border-red-500 text-red-700 bg-red-50"
                        : "border-blue-500 text-blue-600 bg-blue-50";
                    return (
                      <div className="flex flex-col gap-1" data-testid={`booking-status-${p.tracking_number}`}>
                        <span className={`text-[9px] uppercase tracking-widest border px-2 py-0.5 mono ${tone}`}>
                          {bs.replaceAll("_", " ")}
                        </span>
                        {p.booking_number && (
                          <span className="text-[10px] mono text-muted-foreground">{p.booking_number}</span>
                        )}
                        {p.service_type && (
                          <span className="text-[10px] text-muted-foreground">{p.service_type}</span>
                        )}
                      </div>
                    );
                  })() : (<span className="text-[10px] text-muted-foreground">—</span>)}
                </td>
                <td className="p-4">
                  {p.direction === "outward" ? (() => {
                    if (!p.booked_at || !p.expected_delivery_at) {
                      return (
                        <span className="text-[9px] uppercase tracking-widest border px-2 py-0.5 mono border-slate-300 text-slate-500" data-testid={`sla-${p.tracking_number}`}>
                          Not started
                        </span>
                      );
                    }
                    const eta = new Date(p.expected_delivery_at);
                    const now = new Date();
                    const booked = new Date(p.booked_at);
                    const totalMs = eta - booked;
                    const elapsed = now - booked;
                    const pct = totalMs > 0 ? elapsed / totalMs : 0;
                    let tone = "border-emerald-500 text-emerald-700 bg-emerald-50";
                    let label = "On time";
                    if (pct >= 1) { tone = "border-red-600 text-red-700 bg-red-50"; label = "Breached"; }
                    else if (pct >= 0.9) { tone = "border-amber-500 text-amber-700 bg-amber-50"; label = "Warning"; }
                    return (
                      <div className="flex flex-col gap-1" data-testid={`sla-${p.tracking_number}`}>
                        <span className={`text-[9px] uppercase tracking-widest border px-2 py-0.5 mono ${tone}`}>
                          {label}
                        </span>
                        <span className="text-[10px] mono text-muted-foreground">
                          ETA {eta.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    );
                  })() : (<TatChip parcel={p} compact />)}
                </td>
                <td className="p-4 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td className="p-4 whitespace-nowrap">
                  <button data-testid={`parcel-open-${p.tracking_number}`} onClick={() => setSelected(p)} className="text-xs mono underline hover:text-primary">open →</button>
                  <button
                    data-testid={`parcel-row-email-${p.tracking_number}`}
                    onClick={(e) => { e.stopPropagation(); emailStatus(p); }}
                    disabled={emailingId === p.id}
                    className="ml-2 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex items-center align-middle disabled:opacity-40"
                    title="Email status to sender + recipient"
                  ><Mail size={13} /></button>
                  {canDelete && p.status !== "delivered" && (
                    <button
                      data-testid={`parcel-row-delete-${p.tracking_number}`}
                      onClick={(e) => { e.stopPropagation(); setPendingDelete(p); }}
                      className="ml-2 text-muted-foreground hover:text-primary p-1 rounded transition-colors inline-flex items-center align-middle"
                      title="Delete parcel"
                    ><Trash2 size={13} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls · server-side (First/Prev/Next/Last · Page X of Y · Go to page) */}
      <div className="shrink-0 pt-2 border-t border-border bg-white mt-auto">
        {total > 0 && (
          <ServerPagination
            page={page} pageSize={pageSize} total={total} pages={pages}
            onPageChange={(n) => setPage(n)}
            onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem("parcels.pageSize", String(s)); setPage(1); }}
            storageKey="parcels.pageSize" testidPrefix="parcels-pg"
          />
        )}
      </div>
      </div>{/* /flex-col enterprise grid */}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setEditing(false); setEdit(null); } }}>
        <DialogContent data-testid="parcel-detail" className="rounded-md max-w-2xl max-h-[90vh] overflow-y-auto p-6">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <DialogTitle className="font-display text-3xl">Parcel · <span className="mono text-primary">{selected?.tracking_number}</span></DialogTitle>
              {selected && !editing && (
                <div className="flex items-center gap-2 pt-1">
                  {selected.status !== "delivered" && selected.status !== "returned" && (
                    <Button size="sm" variant="outline" data-testid="parcel-edit-btn" onClick={startEdit}
                      className="rounded-md text-xs h-8"><Edit3 size={13} className="mr-1.5" /> Edit</Button>
                  )}
                  {canDelete && selected.status !== "delivered" && (
                    <Button size="sm" variant="outline" data-testid="parcel-delete-btn" onClick={deleteParcel}
                      className="rounded-md text-xs h-8 border-primary text-primary hover:bg-primary hover:text-white">
                      <Trash2 size={13} className="mr-1.5" /> Delete
                    </Button>
                  )}
                  {selected.status === "delivered" && (
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground bg-emerald-50 border border-emerald-300 text-emerald-800 rounded px-2 py-1" data-testid="parcel-locked-badge">
                      Locked · delivered parcels can't be edited or deleted
                    </span>
                  )}
                </div>
              )}
              {selected && editing && (
                <div className="flex items-center gap-2 pt-1">
                  <Button size="sm" variant="outline" data-testid="parcel-edit-cancel" onClick={() => { setEditing(false); setEdit(null); }}
                    className="rounded-md text-xs h-8"><X size={13} className="mr-1.5" /> Cancel</Button>
                  <Button size="sm" data-testid="parcel-edit-save" onClick={saveEdit}
                    className="rounded-md text-xs h-8 bg-primary text-primary-foreground"><Save size={13} className="mr-1.5" /> Save</Button>
                </div>
              )}
            </div>
          </DialogHeader>
          {selected && editing && edit && (
            <div className="space-y-4 text-sm" data-testid="parcel-edit-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <EditField label="Reference ID (internal)" value={edit.tracking_number} onChange={(v)=>setEdit({...edit, tracking_number:v})} testid="edit-tracking" mono />
                <EditField label="AWB / Consignment #" value={edit.awb_number} onChange={(v)=>setEdit({...edit, awb_number:v})} testid="edit-awb" mono />
                <div>
                  <div className="overline mb-1">Courier</div>
                  <CourierSelect value={edit.courier} onChange={(v)=>setEdit({...edit, courier:v})} testid="edit-courier" />
                </div>
                <EditField label="Sender name" value={edit.sender_name} onChange={(v)=>setEdit({...edit, sender_name:v})} testid="edit-sender-name" />
                <EditField label="Sender phone" value={edit.sender_phone} onChange={(v)=>setEdit({...edit, sender_phone:v})} testid="edit-sender-phone" />
                <EditField label="Recipient name" value={edit.recipient_name} onChange={(v)=>setEdit({...edit, recipient_name:v})} testid="edit-recipient-name" />
                <EditField label="Department" value={edit.recipient_department} onChange={(v)=>setEdit({...edit, recipient_department:v})} testid="edit-recipient-dept" />
                <EditField label="Phone" value={edit.recipient_phone} onChange={(v)=>setEdit({...edit, recipient_phone:v})} testid="edit-recipient-phone" />
                <EditField label="Weight (kg)" type="number" step="0.1" value={edit.weight_kg} onChange={(v)=>setEdit({...edit, weight_kg: parseFloat(v)||0})} testid="edit-weight" mono />
                <EditField label="# Items" type="number" step="1" value={edit.item_count} onChange={(v)=>setEdit({...edit, item_count: Math.max(1, parseInt(v)||1)})} testid="edit-item-count" mono />
                <EditField label="Dimensions" value={edit.dimensions} onChange={(v)=>setEdit({...edit, dimensions:v})} testid="edit-dims" />
              </div>
              <EditField label="Notes" value={edit.notes} onChange={(v)=>setEdit({...edit, notes:v})} testid="edit-notes" />
              <div data-testid="edit-shipment-type-wrapper">
                <ShipmentTypeRadio
                  value={edit.shipment_type || "Personal"}
                  onChange={(v) => setEdit({ ...edit, shipment_type: v })}
                  prefix="edit"
                />
              </div>
              <ItemBreakdownEditor
                value={edit.items}
                onChange={(items) => {
                  const totalCount = items && items.length ? items.reduce((n,r) => n + (Number(r.count)||0), 0) : null;
                  const totalWeight = items && items.length ? items.reduce((n,r) => n + ((Number(r.weight_kg)||0) * (Number(r.count)||0)), 0) : 0;
                  setEdit({
                    ...edit,
                    items,
                    item_count: totalCount || edit.item_count,
                    weight_kg: totalWeight > 0 ? Math.round(totalWeight * 1000) / 1000 : edit.weight_kg,
                  });
                }}
                prefix="edit-items"
              />
            </div>
          )}
          {selected && !editing && (
            <div className="space-y-4 text-sm">
              {selected.priority && (
                <div className="flex items-center gap-2 bg-primary text-primary-foreground px-3 py-2 rounded-md" data-testid="priority-banner">
                  <Zap size={16} strokeWidth={2.5} />
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-85">Priority parcel</div>
                    <div className="text-xs">Admin + recipient are auto-notified every 4 hours until this parcel is delivered.</div>
                  </div>
                  {selected.priority_rate != null && (
                    <div className="text-right">
                      <div className="text-[9px] uppercase tracking-widest opacity-80">Manual rate</div>
                      <div className="font-mono font-bold">₹{selected.priority_rate}</div>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Info k="Direction" v={selected.direction} />
                <Info k="Courier" v={selected.courier} mono />
                <Info k="Weight" v={`${selected.weight_kg}kg`} mono />
                <Info k="# Items" v={selected.item_count || 1} mono />
                <Info k="Type" v={selected.parcel_type} />
                <Info k="Sender" v={`${selected.sender_name} · ${selected.sender_phone||""}`} />
                <Info k="Recipient" v={`${selected.recipient_name} · ${selected.recipient_department||""}`} />
                <div>
                  <div className="overline">TAT</div>
                  <div className="mt-1"><TatChip parcel={selected} /></div>
                </div>
              </div>
              {Array.isArray(selected.items) && selected.items.length > 0 && (
                <div className="border border-border rounded-md p-3 bg-muted/20" data-testid="parcel-items-summary">
                  <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <div className="overline flex items-center gap-1.5"><Layers size={12} className="text-primary" /> Item breakdown</div>
                    <div className="flex items-center gap-3">
                      {selected.items_total_weight_kg > 0 && (
                        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                          Weight: <span className="mono text-primary">{selected.items_total_weight_kg} kg</span>
                        </div>
                      )}
                      {selected.allow_split_delivery && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 rounded" data-testid="split-enabled-badge">
                          <Split size={9} /> Split enabled
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.items.map((it, i) => (
                      <span key={i} className="inline-flex items-center gap-1 bg-white border border-border rounded px-2 py-1 text-xs">
                        <span className="font-bold text-primary mono">{it.count}×</span>
                        <span>{it.type}</span>
                        {it.weight_kg ? <span className="text-muted-foreground mono">· {it.weight_kg}kg</span> : null}
                        {it.dimensions ? <span className="text-muted-foreground">· {it.dimensions}</span> : null}
                        {it.description && <span className="text-muted-foreground text-[10px]">· {it.description}</span>}
                      </span>
                    ))}
                  </div>
                  {Array.isArray(selected.delivered_items) && selected.delivered_items.length > 0 && (
                    <div className="mt-3 border-t border-border pt-2 space-y-1" data-testid="delivered-legs">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Delivered so far</div>
                      {selected.delivered_items.map((leg, i) => (
                        <div key={i} className="text-xs flex flex-wrap gap-1 items-center">
                          <span className="mono text-[10px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">#{leg.seq}</span>
                          <span className="font-mono">{new Date(leg.at).toLocaleString()}</span>
                          <span className="text-muted-foreground">·</span>
                          {(leg.items || []).map((it, j) => (
                            <span key={j} className="text-primary font-semibold">{it.count}×{it.type}</span>
                          ))}
                          <span className="text-muted-foreground">· signed {leg.signed_by}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {selected.tracking_url && (
                <a href={selected.tracking_url} target="_blank" rel="noreferrer"
                   data-testid="track-external"
                   className="inline-flex items-center gap-2 text-primary hover:underline text-sm">
                  <PackageCheck size={14} /> Track on {selected.courier} website →
                </a>
              )}
              {selected.condition && <ConditionSummary condition={selected.condition} />}
              {selected.tracking_number && (
                <div className="border border-border rounded-md p-3 bg-red-50/40" data-testid="public-track-share">
                  <div className="overline mb-2 flex items-center gap-1.5"><Globe size={12} className="text-primary" /> Public Tracking Link</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[11px] mono bg-white border border-border rounded px-2 py-1 flex-1 min-w-0 truncate">
                      {`${window.location.origin}/track/${selected.tracking_number}`}
                    </code>
                    <Button
                      type="button" size="sm" variant="outline" data-testid="copy-public-track-link"
                      className="rounded-md text-xs h-8"
                      onClick={() => {
                        const url = `${window.location.origin}/track/${selected.tracking_number}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Public track link copied");
                      }}
                    ><Copy size={12} className="mr-1.5" /> Copy</Button>
                    <a
                      href={`/track/${selected.tracking_number}`} target="_blank" rel="noreferrer"
                      data-testid="open-public-track-link"
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-primary text-primary hover:bg-primary hover:text-white rounded-md px-3 h-8"
                    ><Globe size={12} /> Open</a>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-2">Share this URL with the recipient or sender — no login required.</div>
                </div>
              )}
              {selected.photo_url && (
                <img src={fileUrl(selected.photo_url)} alt="parcel" className="max-h-56 border border-border" />
              )}
              <div>
                <div className="overline mb-2">Timeline</div>
                <ol
                  data-testid="parcel-timeline"
                  className="space-y-2 border-l-2 border-primary/40 pl-4 max-h-56 overflow-y-auto pr-2"
                >
                  {(selected.timeline||[]).map((t, i) => (
                    <li key={i} className="text-xs">
                      <div className="font-semibold uppercase tracking-widest">{t.status?.replace(/_/g," ")}</div>
                      <div className="text-muted-foreground mono">{new Date(t.at).toLocaleString()} · {t.by}</div>
                      {t.remarks && <div>{t.remarks}</div>}
                    </li>
                  ))}
                </ol>
              </div>
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="overline">Actions</div>
                  <div className="flex gap-2">
                    {selected.status !== "delivered" && selected.direction === "inward" && (
                      <Button data-testid="open-delivery-dialog" onClick={() => setDeliverOpen(true)}
                        className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut">
                        <PenLine size={14} className="mr-2" /> Sign & Deliver
                      </Button>
                    )}
                    {selected.status !== "delivered" && selected.direction === "inward" && selected.allow_split_delivery && Array.isArray(selected.items) && selected.items.length > 0 && (
                      <Button data-testid="open-split-dialog" onClick={() => setSplitOpen(true)}
                        variant="outline"
                        className="rounded-md border-primary text-primary hover:bg-primary hover:text-white">
                        <Split size={14} className="mr-2" /> Deliver items…
                      </Button>
                    )}
                    {(selected.direction === "outward" || selected.direction === "transfer") && selected.status !== "delivered" && (
                      <PickupRequestButton parcel={selected} testid="open-pickup-dialog" />
                    )}
                    {selected.status === "delivered" && <DownloadReceiptButton parcel={selected} />}
                    <Button
                      data-testid="parcel-email-status-btn"
                      onClick={() => emailStatus(selected)}
                      disabled={emailingId === selected.id}
                      variant="outline"
                      className="rounded-md border-primary text-primary hover:bg-primary hover:text-white text-xs h-9"
                      title="Email current status to sender + recipient"
                    >
                      <Mail size={13} className="mr-1.5" /> {emailingId === selected.id ? "Sending…" : "Email status"}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="border-t border-border pt-4 space-y-2">
                <div className="overline">Update status</div>
                <Input data-testid="status-signed-by" placeholder="Signed by (optional)" value={signedBy} onChange={(e)=>setSignedBy(e.target.value)} className="rounded-md border-input" />
                <Input data-testid="status-remarks" placeholder="Remarks (optional)" value={remarks} onChange={(e)=>setRemarks(e.target.value)} className="rounded-md border-input" />
                <div className="flex flex-wrap gap-2 pt-2">
                  {STATUSES.filter(s => s !== selected.status).map((s) => (
                    <Button key={s} data-testid={`mark-${s}`} onClick={() => markStatus(selected.id, s)}
                      className="rounded-md bg-card border border-foreground text-foreground hover:bg-primary hover:text-primary-foreground text-xs">
                      {s.replace(/_/g," ")}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter />
        </DialogContent>
      </Dialog>

      <DeliveryReceiptDialog
        open={deliverOpen}
        onOpenChange={setDeliverOpen}
        parcel={selected}
        onDelivered={() => { setDeliverOpen(false); load(); setSelected(null); }}
      />

      <SplitDeliveryDialog
        open={splitOpen}
        onOpenChange={setSplitOpen}
        parcel={selected}
        onDelivered={async () => {
          setSplitOpen(false);
          if (selected?.id) {
            try {
              const { data } = await api.get(`/parcels/${selected.id}`);
              setSelected(data);
            } catch { /* ignore */ }
          }
          load();
        }}
      />

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete parcel"
        subject={pendingDelete?.tracking_number}
        description="This parcel record will be removed. The action is recorded in the audit log."
        onConfirm={confirmDeleteParcel}
      />

      <BookParcelDialog
        open={bookOpen}
        onClose={() => setBookOpen(false)}
        selectedParcels={items.filter((p) => selectedIds[p.id])}
        onBooked={() => { setSelectedIds({}); load(); }}
      />
    </PageShell>
  );
}

const Info = ({ k, v, mono }) => (
  <div>
    <div className="overline">{k}</div>
    <div className={mono?"mono":""}>{v || "—"}</div>
  </div>
);

function EditField({ label, value, onChange, type = "text", step, mono, testid }) {
  return (
    <label className="block">
      <span className="overline">{label}</span>
      <Input data-testid={testid} type={type} step={step} value={value ?? ""} onChange={(e)=>onChange(e.target.value)}
        className={`mt-1 rounded-md border-input focus-visible:ring-primary ${mono ? "font-mono" : ""}`} />
    </label>
  );
}

function ConditionSummary({ condition }) {
  const c = condition || {};
  const damaged = c.package_condition === "Damaged" || c.seal_condition === "Broken"
    || c.water_damage || c.leakage || c.tampered || c.missing_labels;
  const flags = [
    ["water_damage", "Water Damage"],
    ["leakage", "Leakage"],
    ["tampered", "Tampered"],
    ["missing_labels", "Missing Labels"],
    ["fragile_sticker", "Fragile Sticker"],
    ["handle_with_care", "Handle With Care"],
  ].filter(([k]) => c[k]);
  return (
    <div
      className={`border rounded-md p-3 ${damaged ? "border-primary bg-red-50/60" : "border-border bg-muted/20"}`}
      data-testid="parcel-condition-summary"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="overline">Parcel condition</div>
        {damaged && <span className="text-[9px] font-bold uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded">Damaged</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
        <span><span className="text-muted-foreground">Package:</span> <b>{c.package_condition || "—"}</b></span>
        <span><span className="text-muted-foreground">Seal:</span> <b>{c.seal_condition || "—"}</b></span>
        {c.damage_type && <span><span className="text-muted-foreground">Damage:</span> <b>{c.damage_type}</b></span>}
      </div>
      {flags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {flags.map(([k, label]) => (
            <span key={k} className="text-[10px] uppercase tracking-widest font-semibold bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 rounded">
              {label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
